// Turning the raw `alerts` array of a /api/weather snapshot into something an
// operator can read (#92). The banner itself only ever shows the one-line
// `summary` the Lambda builds; these helpers back the expandable detail.
//
// Three properties of the stored data drive every decision here:
//
//   1. Meteoalarm publishes one CAP <entry> per warned region, so a single
//      warning covering three regions arrives as three entries. The live feed
//      currently returns 9 entries for what an operator would call 3 warnings.
//
//   2. Which regions those entries name is only recoverable once the deployed
//      Lambda carries the `areaDesc` capture from #56 — until then the entries
//      of a multi-region warning are indistinguishable, and listing them
//      verbatim shows the same warning three times over.
//
//   3. DynamoDB does not preserve the key order of a map attribute, so the same
//      alert comes back with its fields in a different order from one poll to
//      the next. `JSON.stringify(alert)` is therefore NOT a usable identity —
//      the signature below names every field explicitly.

// Meteoalarm severity maps to a colour in lambda_weather.py (ALERT_LEVELS);
// this is the same ranking that function uses to pick `highest_alert_color`.
export const ALERT_COLOR_RANK = { red: 3, orange: 2, yellow: 1 };

// Dot colours for the detail rows, matched to the banner's own three tints.
export const ALERT_DOT_COLOR = {
  red: '#dc2626',
  orange: '#ea580c',
  yellow: '#ca8a04',
};

const SIG_SEPARATOR = ' ';

/**
 * Identity of an alert timestamp, truncated to the minute.
 *
 * Two entries of the same warning can differ by seconds — the live feed carries
 * one High Temperature warning expiring at 21:59:00 and another at 21:59:59.
 * Both render as "21:59", so treating them as distinct would put two visually
 * identical rows in the list, which is exactly what this is meant to prevent.
 *
 * An unparseable timestamp is compared verbatim rather than discarded: the
 * Lambda deliberately keeps alerts whose expiry it could not parse, and two
 * such alerts are still the same alert if the raw strings match.
 */
export function alertTimeKey(iso) {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return String(iso);
  return String(Math.floor(ms / 60000));
}

/** Field-ordered identity for one alert — see note 3 above. */
export function alertSignature(alert) {
  return [
    alert?.event ?? '',
    alert?.severity ?? '',
    alert?.certainty ?? '',
    alert?.color ?? '',
    alertTimeKey(alert?.onset),
    alertTimeKey(alert?.expires),
  ].join(SIG_SEPARATOR);
}

// Most severe first. Within one colour the warning that started earliest comes
// first, so a list read top to bottom is also read in the order it arrived.
// A missing or unparseable onset sorts last rather than to 1970.
function onsetOrder(iso) {
  const ms = Date.parse(iso ?? '');
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

function compareGroups(a, b) {
  const rank = (ALERT_COLOR_RANK[b.color] ?? 0) - (ALERT_COLOR_RANK[a.color] ?? 0);
  if (rank !== 0) return rank;
  const onset = onsetOrder(a.onset) - onsetOrder(b.onset);
  if (onset !== 0) return onset;
  return a.event.localeCompare(b.event);
}

/**
 * Collapse the raw alert entries into one group per distinct warning.
 *
 * Each group keeps `count` — how many regional entries it was built from — and
 * `regions`, the names of those regions where they are known. `count > 1` with
 * an empty `regions` is the pre-#56-deploy state: three regions are warned and
 * the snapshot does not record which. The caller must not present that as a
 * single warning; it says "3 regional warnings, regions not recorded".
 *
 * Returns [] for null / a non-array, which is what `alerts` is when the feed
 * could not be reached (#57) — the banner handles that case on its own.
 */
export function groupAlerts(alerts) {
  if (!Array.isArray(alerts)) return [];

  const groups = new Map();
  for (const alert of alerts) {
    if (!alert || typeof alert !== 'object') continue;

    const signature = alertSignature(alert);
    let group = groups.get(signature);
    if (!group) {
      group = {
        event: alert.event || 'Unknown',
        severity: alert.severity || 'Unknown',
        certainty: alert.certainty || 'Unknown',
        color: alert.color || 'yellow',
        onset: alert.onset ?? null,
        expires: alert.expires ?? null,
        regions: [],
        count: 0,
      };
      groups.set(signature, group);
    }

    group.count += 1;
    const region = typeof alert.areaDesc === 'string' ? alert.areaDesc.trim() : '';
    if (region && !group.regions.includes(region)) group.regions.push(region);
  }

  const grouped = [...groups.values()];
  grouped.forEach(group => group.regions.sort((a, b) => a.localeCompare(b)));
  return grouped.sort(compareGroups);
}

/**
 * Format a CAP timestamp for display, following the day-first en-GB convention
 * the charts already use rather than the browser locale, so the same instant
 * reads identically everywhere in the dashboard.
 *
 * Returns null when there is no timestamp, and the raw string when there is one
 * that cannot be parsed — showing "2026-13-45" is more use to an operator than
 * silently dropping the field.
 */
export function formatAlertTime(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
